/**
 * TanStack Query hooks for site management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { sitesApi } from '../api';
import type { CreateSiteRequest, UpdateSiteRequest, MigrateDbRequest, MigrationPreflightRequest, UpdateSiteSettingsRequest } from '../types';

// ============================================================================
// Query Keys
// ============================================================================

export const siteKeys = {
  all: ['sites'] as const,
  lists: () => [...siteKeys.all, 'list'] as const,
  list: () => [...siteKeys.lists()] as const,
  details: () => [...siteKeys.all, 'detail'] as const,
  detail: (slug: string) => [...siteKeys.details(), slug] as const,
  settings: (slug: string) => [...siteKeys.all, 'settings', slug] as const,
  themes: () => [...siteKeys.all, 'themes'] as const,
  migrationStatus: (slug: string) => [...siteKeys.all, 'migration-status', slug] as const,
  deployState: (slug: string) => [...siteKeys.all, 'deploy-state', slug] as const,
};

// ============================================================================
// Sites Hooks
// ============================================================================

/** Fetch all sites */
export function useSites() {
  return useQuery({
    queryKey: siteKeys.list(),
    queryFn: () => sitesApi.list(),
  });
}

/** Fetch a single site by slug */
export function useSite(slug: string) {
  return useQuery({
    queryKey: siteKeys.detail(slug),
    queryFn: () => sitesApi.get(slug),
    enabled: !!slug,
  });
}

/**
 * AB#820: saved-but-unpublished changes. No mutation invalidates this key —
 * content, settings and theme writes each invalidate their own — so freshness
 * rides on staleTime + window focus, plus the live-event fan-out in
 * `utils/liveEvents.ts` when a stream is connected.
 */
export function useDeployState(slug: string) {
  return useQuery({
    queryKey: siteKeys.deployState(slug),
    queryFn: () => sitesApi.deployState(slug),
    enabled: !!slug,
    staleTime: 30_000,
  });
}

/** Create a new site */
export function useCreateSite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateSiteRequest) => sitesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: siteKeys.all });
    },
  });
}

/** Update an existing site */
export function useUpdateSite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ slug, data }: { slug: string; data: UpdateSiteRequest }) =>
      sitesApi.update(slug, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: siteKeys.all });
      queryClient.invalidateQueries({
        queryKey: siteKeys.detail(variables.slug),
      });
    },
  });
}

/** Delete a site */
export function useDeleteSite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (slug: string) => sitesApi.delete(slug),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: siteKeys.all });
    },
  });
}

/** Migrate site database to a new database type */
export function useMigrateSiteDb() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ slug, data }: { slug: string; data: MigrateDbRequest }) =>
      sitesApi.migrate(slug, data),
    onSuccess: (_, variables) => {
      // Invalidate site queries to reflect new db_type
      queryClient.invalidateQueries({ queryKey: siteKeys.all });
      queryClient.invalidateQueries({
        queryKey: siteKeys.detail(variables.slug),
      });
    },
  });
}

// ============================================================================
// Site Settings Hooks
// ============================================================================

/** Fetch site settings */
export function useSiteSettings(slug: string) {
  return useQuery({
    queryKey: siteKeys.settings(slug),
    queryFn: () => sitesApi.getSettings(slug),
    enabled: !!slug,
  });
}

/** Update site settings */
export function useUpdateSiteSettings(slug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateSiteSettingsRequest) =>
      sitesApi.updateSettings(slug, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: siteKeys.settings(slug) });
    },
  });
}

/** List available themes. `withFiles` also returns each theme's file tree. */
export function useThemes(withFiles = false) {
  return useQuery({
    queryKey: [...siteKeys.themes(), withFiles] as const,
    queryFn: () => sitesApi.listThemes(withFiles),
  });
}

/** Trigger SSG build */
export function useBuildSite(slug: string) {
  return useMutation({
    mutationFn: (body: { preview_theme_id?: number; preview_theme?: string } | void) =>
      sitesApi.build(slug, body ?? undefined),
  });
}

// ============================================================================
// Migration Status Hooks
// ============================================================================

/**
 * Poll migration job status.
 * Automatically polls while migration is in progress (pending or running).
 * @param slug - Site slug
 * @param enabled - Whether to enable polling (e.g., after starting migration)
 */
export function useMigrationStatus(slug: string, enabled = false) {
  return useQuery({
    queryKey: siteKeys.migrationStatus(slug),
    queryFn: () => sitesApi.getMigrationStatus(slug),
    enabled: enabled && !!slug,
    // A migration keeps going while the tab is in the background; keep polling
    // so the completion toast/navigation fires when the user comes back.
    refetchIntervalInBackground: true,
    refetchInterval: (query) => {
      // Poll every 2 seconds while migration is in progress
      const status = query.state.data?.data?.status;
      if (status === 'pending' || status === 'running') {
        return 2000;
      }
      // Stop polling when completed, failed, or cancelled
      return false;
    },
  });
}

/** Cancel an in-progress migration */
export function useCancelMigration(slug: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (expectedJobId: number) => sitesApi.cancelMigration(slug, expectedJobId),
    onSuccess: () => {
      // Refresh migration status to show cancelled state
      queryClient.invalidateQueries({ queryKey: siteKeys.migrationStatus(slug) });
    },
  });
}

/** Read-only server review; execution revalidates rather than trusting a cached approval. */
export function useMigrationPreflight(slug: string, request: MigrationPreflightRequest, actorId: string, enabled = true, accountId = '') {
  return useQuery({ queryKey: [...siteKeys.all, 'migration-preflight', actorId, accountId, slug, request], queryFn: () => sitesApi.migrationPreflight(slug, request), enabled: !!slug && !!actorId && enabled, retry: false, staleTime: 0 });
}
